// Phone numbers are stored as "<country code> <national number>", e.g. "+60 12-882 1034".
// The national number is kept without its trunk "0" so it can be dialled internationally.

export const COUNTRY_CODES = [
  { code: "+60", country: "Malaysia", iso: "MY" },
  { code: "+65", country: "Singapore", iso: "SG" },
  { code: "+673", country: "Brunei", iso: "BN" },
  { code: "+62", country: "Indonesia", iso: "ID" },
  { code: "+66", country: "Thailand", iso: "TH" },
  { code: "+63", country: "Philippines", iso: "PH" },
  { code: "+84", country: "Vietnam", iso: "VN" },
  { code: "+86", country: "China", iso: "CN" },
  { code: "+852", country: "Hong Kong", iso: "HK" },
  { code: "+886", country: "Taiwan", iso: "TW" },
  { code: "+81", country: "Japan", iso: "JP" },
  { code: "+82", country: "South Korea", iso: "KR" },
  { code: "+91", country: "India", iso: "IN" },
  { code: "+61", country: "Australia", iso: "AU" },
  { code: "+64", country: "New Zealand", iso: "NZ" },
  { code: "+44", country: "United Kingdom", iso: "GB" },
  { code: "+1", country: "United States / Canada", iso: "US" },
];

export const DEFAULT_COUNTRY_CODE = "+60";

const digits = (value = "") => value.replace(/\D/g, "");

// Longest matching code first, so "+673" isn't read as "+6".
const CODES_BY_LENGTH = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);

export const splitPhone = (phone = "") => {
  const trimmed = phone.trim();
  const match = CODES_BY_LENGTH.find((c) => trimmed.startsWith(c.code));
  if (match) return { code: match.code, number: trimmed.slice(match.code.length).trim() };
  return { code: DEFAULT_COUNTRY_CODE, number: trimmed.replace(/^0/, "") };
};

// Drops the trunk "0" a local caller would dial (012-... -> 12-...).
export const normalizeNumber = (number = "") => number.trim().replace(/^0+/, "");

// Malaysian numbers get the familiar grouping: 12-345 6789, 11-2345 6789, 3-2123 4567, 82-123 456.
const formatMalaysian = (d) => {
  if (d.startsWith("11") && d.length === 10) return `11-${d.slice(2, 6)} ${d.slice(6)}`;
  if (d.startsWith("1") && d.length === 9) return `${d.slice(0, 2)}-${d.slice(2, 5)} ${d.slice(5)}`;
  if (d.startsWith("3") && d.length === 9) return `3-${d.slice(1, 5)} ${d.slice(5)}`; // Klang Valley
  if (d.length === 8) return `${d.slice(0, 2)}-${d.slice(2, 5)} ${d.slice(5)}`; // other landlines, e.g. 82 Kuching
  return d;
};

export const joinPhone = (code, number) => {
  const n = normalizeNumber(number);
  if (!n) return code;
  return `${code} ${code === "+60" ? formatMalaysian(digits(n)) : n}`;
};

export const hasNumber = (phone) => digits(splitPhone(phone).number).length > 0;

export const formatPhone = (phone, fallback = "—") => (phone && hasNumber(phone) ? phone : fallback);

// For tel: links, e.g. "tel:+60128821034".
export const telHref = (phone) => {
  const { code, number } = splitPhone(phone);
  return `tel:${code}${digits(number)}`;
};

// For wa.me links: international digits only, e.g. "60128821034".
export const whatsappNumber = (phone) => {
  const { code, number } = splitPhone(phone);
  return `${digits(code)}${digits(number)}`;
};

// Search helper: "0128821034", "128821034", "60128821034" and "+60 12-882 1034" all match.
export const phoneMatches = (phone, query) => {
  const q = digits(query);
  if (q.length < 2) return false;
  const { code, number } = splitPhone(phone);
  const national = digits(number);
  return `${digits(code)}${national}`.includes(q) || national.includes(q.replace(/^0+/, "")) || `0${national}`.includes(q);
};
